import React, { useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { adminActivity } from "@/lib/api";
import { SSEContext } from "@/components/AppShell";
import {
  UserPlus, UserCheck, CreditCard, CheckCircle2, XCircle, Clock, AlertTriangle, Activity,
} from "lucide-react";

const ICON = {
  user_signup:          { I: UserPlus,       color: "text-sky-300",     bg: "bg-sky-500/10",     ring: "ring-sky-400/30" },
  user_approved:        { I: UserCheck,      color: "text-emerald-300", bg: "bg-emerald-500/10", ring: "ring-emerald-400/30" },
  payment_submitted:    { I: CreditCard,     color: "text-amber-300",   bg: "bg-amber-500/10",   ring: "ring-amber-400/30" },
  payment_verified:     { I: CheckCircle2,   color: "text-emerald-300", bg: "bg-emerald-500/10", ring: "ring-emerald-400/30" },
  payment_failed:       { I: XCircle,        color: "text-red-300",     bg: "bg-red-500/10",     ring: "ring-red-400/30" },
  trial_expiring:       { I: Clock,          color: "text-amber-200",   bg: "bg-amber-500/10",   ring: "ring-amber-400/30" },
  subscription_expired: { I: AlertTriangle,  color: "text-red-300",     bg: "bg-red-500/10",     ring: "ring-red-400/30" },
};

const fmtAgo = (iso) => {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return `${Math.max(1, Math.floor(diff / 1000))}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
};

export default function LiveActivityFeed() {
  const { data, refetch } = useQuery({
    queryKey: ["admin-activity"],
    queryFn: () => adminActivity({ limit: 50 }),
  });
  const { lastAdminActivity } = useContext(SSEContext);
  const [items, setItems] = useState([]);

  // Seed from initial fetch
  useEffect(() => {
    if (data?.events) setItems(data.events);
  }, [data]);

  // Prepend new SSE events (dedup by id)
  useEffect(() => {
    if (!lastAdminActivity?.id) return;
    setItems((prev) => {
      if (prev.some((e) => e.id === lastAdminActivity.id)) return prev;
      return [lastAdminActivity, ...prev].slice(0, 50);
    });
  }, [lastAdminActivity]);

  return (
    <section
      data-testid="admin-activity-feed"
      className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/50">
          <Activity className="size-3.5 text-amber-300" />
          Live activity
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
        </div>
        <button
          onClick={() => refetch()}
          className="text-xs text-white/40 hover:text-white/80"
          data-testid="admin-activity-refresh"
        >
          refresh
        </button>
      </div>
      <ul className="space-y-1 max-h-72 overflow-y-auto pr-1" data-testid="admin-activity-list">
        {items.length === 0 && (
          <li className="text-xs text-white/40 py-2">No activity yet — events stream live as they happen.</li>
        )}
        {items.map((ev) => {
          const cfg = ICON[ev.event_type] || ICON.user_signup;
          const I = cfg.I;
          return (
            <li
              key={ev.id}
              data-testid={`admin-activity-item-${ev.event_type}`}
              className={`flex items-start gap-2.5 px-2 py-1.5 rounded-md ring-1 ${cfg.bg} ${cfg.ring}`}
            >
              <I className={`size-4 mt-0.5 shrink-0 ${cfg.color}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white/90 truncate">{ev.title}</div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">
                  {ev.event_type.replace(/_/g, " ")} · {fmtAgo(ev.created_at)}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
